import isEqual from "lodash/isEqual"
import { stripRuleSetRules } from "../rule-sets/strip-rule-set-rules"

const EMPTY_RULE_SET_IDS = []

export function getRuleSetIdsForVariant(variant) {
    if (variant?.ruleSetIds) return variant.ruleSetIds
    if (variant?.ruleSetId) return [variant.ruleSetId]
    return EMPTY_RULE_SET_IDS
}

export function isSettledVariantOverride(serverVariant, override) {
    if (!serverVariant) return false
    if (override.modifierUpdatePending) return false

    let hasOptimisticValue = false
    if (override.ruleSetIds) {
        hasOptimisticValue = true
        if (!areRuleSetIdsEqual(getRuleSetIdsForVariant(serverVariant), override.ruleSetIds)) return false
    }
    if (override.disabledModifierIds) {
        hasOptimisticValue = true
        if (!haveSameIds(serverVariant.disabledModifierIds, override.disabledModifierIds)) return false
        if (serverVariant.status === "calculating") return false
    }
    return hasOptimisticValue
}

export function areRuleSetIdsEqual(a, b) {
    if (a.length !== b.length) return false
    return a.every((id, index) => id === b[index])
}

export function haveSameIds(first = [], second = []) {
    const firstIds = new Set(first)
    const secondIds = new Set(second)
    if (firstIds.size !== secondIds.size) return false
    for (const id of firstIds) {
        if (!secondIds.has(id)) return false
    }
    return true
}

export function mergeRuleSetIds(existingIds, selectedRuleSets) {
    const ids = new Set(existingIds)
    for (const ruleSet of selectedRuleSets) {
        if (ruleSet?.id) ids.add(ruleSet.id)
    }
    return [...ids]
}

export function hasCalculationRelevantRuleSetChange(ruleSet, result, rulesForSave) {
    if ((ruleSet?.defaultAction || "include") !== result.defaultAction) return true
    const previousRules = stripRuleSetRules(ruleSet?.rules || []).map(normalizeRuleForComparison)
    const nextRules = (rulesForSave || []).map(normalizeRuleForComparison)
    // Deep equality rather than JSON.stringify: key-order differences caused false "changed" results
    return !isEqual(previousRules, nextRules)
}

// The edit dialog fills in defaults the stored rules may lack (enabled, action, conditions),
// and GraphQL returns null for absent fields. Canonicalize both sides the same way so a
// round-trip through the dialog only reads as "changed" when something cost-relevant changed.
// Rule name/justification are cosmetic, so they are excluded from the comparison.
function normalizeRuleForComparison({ name: __name, justification: __justification, ...rule }) {
    const cleaned = omitNullValues(rule)
    return {
        ...cleaned,
        type: cleaned.type || "rule",
        enabled: cleaned.enabled !== false,
        conditions: (cleaned.conditions || []).map(omitNullValues),
        action: cleaned.action ? omitNullValues(cleaned.action) : { type: "include" },
    }
}

function omitNullValues(obj) {
    return Object.fromEntries(Object.entries(obj).filter(notNullEntry))
}

function notNullEntry([, value]) {
    return value != null
}

export function buildRecalculationPrompt(variantCount) {
    if (variantCount === 1) {
        return "You changed a rule set attached to this scenario. Recalculate the affected variant now?"
    }
    return `You changed a rule set attached to this scenario. Recalculate the ${variantCount} affected variants now?`
}

export function restoreRuleSetReferenceNames(rules, previousRules) {
    const previousReferenceNames = new Map((previousRules || []).map(toRuleSetReferenceNameEntry).filter(Boolean))
    return (rules || []).map((rule) => restoreRuleSetReferenceName(rule, previousReferenceNames))
}

function toRuleSetReferenceNameEntry(rule) {
    if (rule?.type !== "ruleSetReference" || !rule.ruleSetId || !rule.ruleSetName) return null
    return [rule.ruleSetId, rule.ruleSetName]
}

function restoreRuleSetReferenceName(rule, previousReferenceNames) {
    if (rule?.type !== "ruleSetReference" || rule.ruleSetName || !rule.ruleSetId) return rule
    const ruleSetName = previousReferenceNames.get(rule.ruleSetId)
    if (!ruleSetName) return rule
    return { ...rule, ruleSetName }
}

export function toRuleSetEntry(ruleSet) {
    return [ruleSet.id, ruleSet]
}

export function hasRules(ruleSet) {
    return Boolean(ruleSet?.rules?.length)
}

export function toId(item) {
    return item.id
}

export function byId(targetId) {
    return (item) => item.id === targetId
}

export function notMatchingId(targetId) {
    return (id) => id !== targetId
}

export function toRuleSetForDialog(names) {
    return (id, index) => ({ id, name: names[index] || id })
}
